import type { NewsStory } from './newsStory';

const ATOM_NS = 'http://www.w3.org/2005/Atom';

/** Parse RSS 2.0 or Atom feed XML into stories. */
export function parseNewsRssXml(
  xml: string,
  sourceId: string,
  sourceLabel: string
): NewsStory[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error(`Invalid RSS XML from ${sourceLabel}`);
  }

  const atomEntries = doc.getElementsByTagNameNS(ATOM_NS, 'entry');
  if (atomEntries.length > 0) {
    return parseAtomEntries(atomEntries, sourceId, sourceLabel);
  }

  const rssItems = doc.querySelectorAll('item');
  if (rssItems.length > 0) {
    return parseRss2Items(rssItems, sourceId, sourceLabel);
  }

  throw new Error(`No entries in feed from ${sourceLabel}`);
}

function parseAtomEntries(
  entries: HTMLCollectionOf<Element>,
  sourceId: string,
  sourceLabel: string
): NewsStory[] {
  const stories: NewsStory[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries.item(i);
    if (!entry) continue;
    const title = entry.getElementsByTagNameNS(ATOM_NS, 'title').item(0)?.textContent?.trim();
    if (!title) continue;
    const linkEl =
      entry.querySelector('link[rel="alternate"]') ??
      entry.getElementsByTagNameNS(ATOM_NS, 'link').item(0);
    const href = linkEl?.getAttribute('href')?.trim() ?? '';
    const updated =
      entry.getElementsByTagNameNS(ATOM_NS, 'updated').item(0)?.textContent?.trim() ??
      entry.getElementsByTagNameNS(ATOM_NS, 'published').item(0)?.textContent?.trim();
    stories.push({
      title: normalizeTitle(title),
      link: href,
      sourceId,
      sourceLabel,
      feedRank: stories.length,
      ...(updated ? { publishedAt: updated } : {}),
    });
  }
  return stories;
}

function parseRss2Items(
  items: NodeListOf<Element>,
  sourceId: string,
  sourceLabel: string
): NewsStory[] {
  const stories: NewsStory[] = [];
  items.forEach((item, index) => {
    const title = item.querySelector('title')?.textContent?.trim();
    if (!title) return;
    const link =
      item.querySelector('link')?.textContent?.trim() ??
      item.querySelector('link')?.getAttribute('href')?.trim() ??
      '';
    const pubDate = item.querySelector('pubDate')?.textContent?.trim();
    stories.push({
      title: normalizeTitle(title),
      link,
      sourceId,
      sourceLabel,
      feedRank: index,
      ...(pubDate ? { publishedAt: pubDate } : {}),
    });
  });
  return stories;
}

function normalizeTitle(title: string): string {
  return title.replace(/\s+/g, ' ').trim();
}
